/**
 * Converts a flattened object with keys representing child properties,
 * into a nested object.
 *
 * @public
 *
 * @param target  The source value that will be unflattened.
 *
 * @returns       An expanded object
 */
export function unflatten(target: Record<string, unknown>): Record<string, unknown> {
  let pattern = /(\.?(?<property>[^.[\]{}]+)|\[(?<index>\d+)\])((?<isArray>\[\])|(?<isObject>\{\}))?/g
  let value = {}

  for (let property in target) {
    let currentContainer = value
    let currentProperty
    let isArray = false
    let isObject = false

    // Loop through the possible nested values of the property
    let matches
    while ((matches = pattern.exec(property)) !== null) {

      // Set up the next levels container
      if (currentProperty) {
        if (!currentContainer[currentProperty]) {
          currentContainer[currentProperty] = matches?.groups?.index ? [] : {}
        }

        currentContainer = currentContainer[currentProperty]
      }

      // Define if the value is an array or an object
      isArray = matches?.groups?.isArray != null
      isObject = matches?.groups?.isObject != null

      // Set up the next property
      currentProperty = matches?.groups?.index ?? matches?.groups?.property
    }

    // Set the target value
    let targetValue = target[property]

    // If the target property is an array or an object
    if (isArray) {
      targetValue = unflattenSerializedArray(targetValue)
    }
    if (isObject) {
      targetValue = unflattenSerializedObject(targetValue)
    }

    // Assign the target value
    currentContainer[currentProperty] = targetValue
  }

  return value
}

/**
 * Unflatten the value of a property that ends with `[]` into an array.
 *
 * If the value of the array is serialized as a JSON array or a comma separated array, it will be decoded
 * and returned. If the given value is not a value that can be deserialized, it will instead return the value
 * wrapped in an array.
 *
 * @param value The value that will be deserialized.
 *
 * @returns     The deserialized array.
 */
function unflattenSerializedArray(value: unknown): Array<unknown> {
  if (typeof value === 'string') {
    try {
      let jsonString = value?.toString() ?? ''
      return JSON.parse(jsonString)
    }

    catch {
      return value.split(',')
    }
  }

  return [ value ]
}

/**
 * Unflatten the value of a property that ends with `{}` into an object.
 *
 * If the value of the object is serialized as a JSON object, it will be decoded and returned. If the
 * value is not deserializable, it will instead return an empty object.
 *
 * @param value The value that will be deserialized.
 *
 * @returns     The deserialized object.
 */
function unflattenSerializedObject(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      let jsonString = value?.toString() ?? ''
      return JSON.parse(jsonString)
    }

    catch {
      return {}
    }
  }

  return {}
}
